import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { localStorageKeys } from '@/utils/tokens';
import { LOCAL_STORAGE_FILTERS_AND_SORTING } from '@/utils/configs/healthCheckConfig';
import { saveFilterToLocalStorage } from '@/utils/localStorage';
import { SortDirection } from '@/utils/sortingUtils';

export type SortField = 'vehicleId' | 'departureTime' | 'issues';

export interface SortOption {
  field: SortField;
  direction: SortDirection;
}

export interface StatusItemsSortingState {
  sortOptions: SortOption[];
}

export const defaultStatusItemsSorting: StatusItemsSortingState = {
  sortOptions: [{ field: 'issues', direction: 'desc' }],
};

const initialState: StatusItemsSortingState = {
  ...defaultStatusItemsSorting,
  ...JSON.parse(
    localStorage.getItem(localStorageKeys.statusItemsSorting) || '{}'
  ),
};

const statusItemsSortingSlice = createSlice({
  name: 'statusItemsSorting',
  initialState,
  reducers: {
    setSortOptions(
      state,
      action: PayloadAction<{ field: SortField; direction?: SortDirection }>
    ) {
      const { field, direction } = action.payload;

      const existingIndex = state.sortOptions.findIndex(
        (opt) => opt.field === field
      );

      let newDirection: SortDirection;
      if (
        existingIndex !== -1 &&
        !direction &&
        state.sortOptions[existingIndex]
      ) {
        newDirection =
          state.sortOptions[existingIndex].direction === 'asc' ? 'desc' : 'asc';
      } else {
        newDirection = direction || 'asc';
      }

      if (existingIndex !== -1) {
        state.sortOptions.splice(existingIndex, 1);
      }

      state.sortOptions.push({ field, direction: newDirection });

      saveFilterToLocalStorage(
        localStorageKeys.statusItemsSorting,
        state,
        LOCAL_STORAGE_FILTERS_AND_SORTING
      );
    },
    removeSortField(state, action: PayloadAction<{ field: SortField }>) {
      const { field } = action.payload;

      // At least one sort option must remain active
      if (state.sortOptions.length <= 1) {
        return;
      }

      const existingIndex = state.sortOptions.findIndex(
        (opt) => opt.field === field
      );
      if (existingIndex !== -1) {
        state.sortOptions.splice(existingIndex, 1);

        saveFilterToLocalStorage(
          localStorageKeys.statusItemsSorting,
          state,
          LOCAL_STORAGE_FILTERS_AND_SORTING
        );
      }
    },
    resetSortOptions(state) {
      state.sortOptions = [...defaultStatusItemsSorting.sortOptions];

      saveFilterToLocalStorage(
        localStorageKeys.statusItemsSorting,
        state,
        LOCAL_STORAGE_FILTERS_AND_SORTING
      );
    },
  },
});

export const { setSortOptions, removeSortField, resetSortOptions } =
  statusItemsSortingSlice.actions;

export default statusItemsSortingSlice.reducer;
